import { Injectable } from '@angular/core';

// Web Serial API type declarations â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
declare global {
  interface Navigator {
    readonly serial: Serial;
  }
  interface Serial extends EventTarget {
    getPorts(): Promise<SerialPort[]>;
    requestPort(options?: SerialPortRequestOptions): Promise<SerialPort>;
  }
  interface SerialPort extends EventTarget {
    open(options: SerialOptions): Promise<void>;
    close(): Promise<void>;
    readonly readable: ReadableStream<Uint8Array> | null;
    readonly writable: WritableStream<Uint8Array> | null;
    getInfo(): SerialPortInfo;
  }
  interface SerialOptions {
    baudRate: number;
    dataBits?: number;
    stopBits?: number;
    parity?: 'none' | 'even' | 'odd' | 'mark' | 'space';
    flowControl?: 'none' | 'hardware';
  }
  interface SerialPortInfo {
    usbVendorId?: number;
    usbProductId?: number;
  }
  interface SerialPortRequestOptions {
    filters?: SerialPortFilter[];
  }
  interface SerialPortFilter {
    usbVendorId?: number;
    usbProductId?: number;
  }
}

const METTLER_TOLEDO_OPTIONS: SerialOptions = {
  baudRate: 2400,
  dataBits: 7,
  stopBits: 2,
  parity: 'none',
  flowControl: 'none',
};

const TERMINATOR = '\r\n';
const PORT_PREFERENCE_KEY = 'cemsys.scale-port-preference';

export interface BrowserPortInfo {
  /** Human-readable label e.g. "USB Serial (VID:0403 PID:6001)" or "Serial Port 1" */
  label: string;
  /** The underlying SerialPort handle, if needed for direct use */
  port: SerialPort;
  identity: string;
}

export interface BrowserScaleResult {
  success: boolean;
  weight: number;
  unit: string;
  /** true = "S S" stable reading; false = "S D" dynamic/unstable */
  stable: boolean;
  errorMessage?: string | null;
}

/**
 * Reads the scale directly from the **browser** using the Web Serial API.
 *
 * WHY: The scale (Mettler Toledo / Ohaus) is plugged into the lab PC via USB/serial.
 * When the app is deployed to Azure the API server has no COM ports, so server-side
 * SerialPort calls always fail. This service runs entirely in the browser and returns
 * the parsed weight value to the Angular component.
 *
 * The session follows the legacy protocol: it initializes the device with
 * "@", "K 3", and "M21 0 0"; waits for the physical Print event ("K C 7");
 * accepts only stable gram readings; and restores key mode before closing.
 * Browser permission and port selection remain browser-local because Web Serial
 * does not expose Windows COM port names to the application.
 */
@Injectable({ providedIn: 'root' })
export class WebSerialScaleService {
  private selectedPort: SerialPort | null = null;

  /** True only in Chrome/Edge â€” Firefox does not support Web Serial API */
  get isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'serial' in navigator;
  }

  /**
   * Returns human-readable labels for all serial ports the user has previously
   * granted access to in this browser.  Uses Web Serial API `getPorts()` +
   * `getInfo()` because the API does not expose OS-level port names (e.g. "COM3").
   *
   * Label format:
   *   - USB device  → "USB Serial (VID:0403 PID:6001)"
   *   - Non-USB     → "Serial Port <n>"  (e.g. built-in RS-232)
   *   - No access   → empty array
   */
  async getGrantedPorts(): Promise<BrowserPortInfo[]> {
    if (!this.isSupported) return [];
    try {
      return (await navigator.serial.getPorts()).map((port, index) => this.toPortInfo(port, index));
    } catch {
      return [];
    }
  }

  async getGrantedPortLabels(): Promise<string[]> {
    return (await this.getGrantedPorts()).map(item => item.label);
  }

  /** Must be called from a user gesture because browser serial authorization is local. */
  async authorizeAndSelectPort(): Promise<BrowserPortInfo> {
    if (!this.isSupported) {
      throw new Error('Web Serial API is not available. Please use Google Chrome or Microsoft Edge.');
    }
    const port = await navigator.serial.requestPort({});
    this.setSelectedPort(port);
    return this.toPortInfo(port, 0);
  }

  async selectGrantedPort(identity: string): Promise<BrowserPortInfo> {
    const matches = (await this.getGrantedPorts()).filter(item => item.identity === identity);
    if (matches.length !== 1) {
      throw new Error(matches.length === 0
        ? 'The selected scale is no longer authorized. Authorize it again.'
        : 'More than one matching scale is authorized. Authorize and select the scale again.');
    }
    this.setSelectedPort(matches[0].port);
    return matches[0];
  }

  /**
   * Reads from an already-granted serial port without opening the browser picker.
   * This is the closest browser-safe equivalent of the legacy popup behavior where
   * the app starts waiting on CaptureScalesContext() and the value appears in the
   * read-only Measured field without an in-popup "Read Weight" button.
   *
   */
  async readWeightFromGrantedPort(signal?: AbortSignal): Promise<BrowserScaleResult> {
    if (!this.isSupported) {
      return {
        success: false,
        weight: 0,
        unit: '',
        stable: false,
        errorMessage: 'Web Serial API is not available. Please use Google Chrome or Microsoft Edge.',
      };
    }
    try {
      return await this.readFromPort(await this.resolveSelectedPort(), signal);
    } catch (error) {
      return this.failure(this.errorMessage(error));
    }
  }

  /**
   * Compatibility entry point for callers that expect the service to prompt for
   * a port when no single authorized scale can be resolved automatically.
   */
  async readWeight(signal?: AbortSignal): Promise<BrowserScaleResult> {
    if (!this.isSupported) {
      return {
        success: false,
        weight: 0,
        unit: '',
        stable: false,
        errorMessage: 'Web Serial API is not available. Please use Google Chrome or Microsoft Edge.',
      };
    }

    try {
      let port: SerialPort;
      try {
        port = await this.resolveSelectedPort();
      } catch {
        port = (await this.authorizeAndSelectPort()).port;
      }
      return await this.readFromPort(port, signal);
    } catch (error) {
      return this.failure(this.errorMessage(error));
    }
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private async resolveSelectedPort(): Promise<SerialPort> {
    const ports = await this.getGrantedPorts();
    if (this.selectedPort) {
      if (ports.some(item => item.port === this.selectedPort)) {
        return this.selectedPort;
      }
      // A USB/hub re-enumeration invalidates the old SerialPort object even when
      // the replacement has the same VID/PID. Do not keep retrying a stale handle.
      this.selectedPort = null;
    }
    const preference = this.loadPreference();
    const matches = preference ? ports.filter(item => this.matchesPreference(item.port, preference)) : ports;
    if (matches.length === 1) {
      this.selectedPort = matches[0].port;
      return this.selectedPort;
    }
    throw new Error(matches.length === 0
      ? 'No authorized scale is selected. Use Change Scale Port to authorize and select it.'
      : 'More than one scale is authorized. Use Change Scale Port to select the scale.');
  }

  private async readFromPort(port: SerialPort, signal?: AbortSignal): Promise<BrowserScaleResult> {
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
    let buffer = '';
    let abortHandler: (() => void) | null = null;
    let portOpened = false;

    try {
      if (signal?.aborted) throw new DOMException('Scale reading cancelled.', 'AbortError');
      await port.open(METTLER_TOLEDO_OPTIONS);
      portOpened = true;
      if (!port.readable || !port.writable) throw new Error('Serial port is not readable/writable.');
      reader = port.readable.getReader();
      writer = port.writable.getWriter();
      abortHandler = () => { void reader?.cancel(); };
      signal?.addEventListener('abort', abortHandler, { once: true });

      const send = async (command: string) => writer!.write(new TextEncoder().encode(command + TERMINATOR));
      const readLine = () => this.readLine(reader!, () => buffer, value => buffer = value, signal);

      await send('@');
      const initializeReply = await readLine();
      if (!initializeReply.startsWith('I4 A ')) throw new Error(`Unexpected scale initialization response: '${initializeReply.trim()}'.`);

      await send('K 3');
      if (await readLine() !== 'K A\r\n') throw new Error('Scale did not acknowledge key-lock mode.');

      await send('M21 0 0');
      if (await readLine() !== 'M21 A\r\n') throw new Error('Scale did not acknowledge key-event mode.');
      while (true) {
        const event = await readLine();
        if (event === 'K C 10\r\n') { await send('T'); continue; }
        if (event === 'K C 5\r\n') { await send('Z'); continue; }
        if (event === 'K C 1\r\n') { await send('DW'); continue; }
        if (event !== 'K C 7\r\n') continue;

        await send('S');
        const result = this.parseStableGramResponse(await readLine());
        if (result.success) return result;
        // A dynamic or invalid Print result does not complete the measurement.
        if (result.errorMessage?.startsWith('Dynamic')) continue;
        return result;
      }
    } catch (error) {
      if (!portOpened && this.selectedPort === port) {
        // The device may have disconnected or been re-enumerated. The next Retry
        // must resolve a current granted port instead of reopening this object.
        this.selectedPort = null;
      }
      return this.failure(this.errorMessage(error));
    } finally {
      signal?.removeEventListener('abort', abortHandler!);
      if (writer) {
        try { await writer.write(new TextEncoder().encode('@' + TERMINATOR)); } catch { /* best effort cleanup */ }
        try { await writer.write(new TextEncoder().encode('K 1' + TERMINATOR)); } catch { /* best effort cleanup */ }
      }
      try { await reader?.cancel(); } catch { /* stream may already be closed */ }
      try { reader?.releaseLock(); } catch { /* ignored */ }
      try { writer?.releaseLock(); } catch { /* ignored */ }
      try { await port.close(); } catch { /* ignored */ }
    }
  }

  private async readLine(reader: ReadableStreamDefaultReader<Uint8Array>, getBuffer: () => string, setBuffer: (value: string) => void, signal?: AbortSignal): Promise<string> {
    while (true) {
      const buffer = getBuffer();
      const index = buffer.indexOf(TERMINATOR);
      if (index >= 0) {
        setBuffer(buffer.slice(index + TERMINATOR.length));
        return buffer.slice(0, index + TERMINATOR.length);
      }
      if (signal?.aborted) throw new DOMException('Scale reading cancelled.', 'AbortError');
      const { value, done } = await reader.read();
      if (done) {
        if (signal?.aborted) throw new DOMException('Scale reading cancelled.', 'AbortError');
        throw new Error('Serial port stream ended before receiving a response.');
      }
      setBuffer(buffer + new TextDecoder().decode(value, { stream: true }));
    }
  }

  private parseStableGramResponse(raw: string): BrowserScaleResult {
    const line = raw.replace(TERMINATOR, '').trim();
    const parts = line.split(/\s+/).filter(part => part.length > 0);
    if (parts.length < 2 || parts[0].toUpperCase() !== 'S') {
      return { success: false, weight: 0, unit: '', stable: false, errorMessage: `Unrecognized scale response: '${line}'` };
    }

    const status = parts[1].toUpperCase();
    if (status === 'D') {
      return this.failure('Dynamic scale reading. Wait for a stable value and press Print again.');
    }
    if (status !== 'S') {
      return { success: false, weight: 0, unit: '', stable: false, errorMessage: `Scale command could not be executed: '${line}'` };
    }

    if (parts.length < 4) {
      return { success: false, weight: 0, unit: '', stable: false, errorMessage: `Unable to parse weight from response: '${line}'` };
    }

    if (parts[3] !== 'g') {
      return this.failure(`Unsupported scale unit '${parts[3]}'. Only grams can be saved.`);
    }
    const weight = Number(parts[2]);
    if (!Number.isFinite(weight)) {
      return { success: false, weight: 0, unit: '', stable: false, errorMessage: `Unable to parse weight value from: '${line}'` };
    }

    return {
      success: true,
      weight,
      unit: 'g',
      stable: true,
    };
  }

  private setSelectedPort(port: SerialPort): void {
    this.selectedPort = port;
    try { localStorage.setItem(PORT_PREFERENCE_KEY, JSON.stringify(port.getInfo())); } catch { /* optional */ }
  }

  private loadPreference(): SerialPortInfo | null {
    try { return JSON.parse(localStorage.getItem(PORT_PREFERENCE_KEY) ?? 'null') as SerialPortInfo | null; } catch { return null; }
  }

  private matchesPreference(port: SerialPort, preference: SerialPortInfo): boolean {
    const info = port.getInfo();
    return info.usbVendorId === preference.usbVendorId && info.usbProductId === preference.usbProductId;
  }

  private toPortInfo(port: SerialPort, index: number): BrowserPortInfo {
    const info = port.getInfo();
    const vid = info.usbVendorId?.toString(16).toUpperCase().padStart(4, '0');
    const pid = info.usbProductId?.toString(16).toUpperCase().padStart(4, '0');
    return {
      port,
      identity: vid && pid ? `usb:${vid}:${pid}` : `serial:${index}`,
      label: vid && pid ? `USB Serial (VID:${vid} PID:${pid})` : `Serial device ${index + 1}`
    };
  }

  private failure(errorMessage: string): BrowserScaleResult {
    return { success: false, weight: 0, unit: '', stable: false, errorMessage };
  }
  private errorMessage(error: unknown): string {
    if (error instanceof DOMException && error.name === 'AbortError') return 'Scale reading cancelled.';
    return error instanceof Error ? error.message : 'Unknown error communicating with the scale.';
  }
}
